// @flow

import React, { useCallback, useContext, useState } from 'react';
import { bind } from '@ta/common.api';
import GlobalNavContext from '@ta/brand.global-nav-context';
import { localize } from '@ta/common.i18n';
import trackEventAJAX from '@ta/common.tracking';
import { Icon } from '@ta/styleguide.icon';
import ResponsiveAttachedOverlay from '@ta/overlays.responsive-attached-overlay';
import classNames from '@ta/common.classnames';
import type { GeoPillSponsorDataProvider } from '@ta/brand.sponsored-geopill';
import GeoPillSearch from '@ta/brand.geopill-search';

import styles from './GeoPill.less';

type Props = {|
  /** The label to display in the geo pill */
  label: string,

  /** The current servlet name */
  servletName: string,

  /** Promise for future geopill sponsor result or null if not enabled */
  sponsorDataProvider?: ?GeoPillSponsorDataProvider,
|};

/**
 * The "Geo Pill" which is a button that displays the current location and can be clicked to open a typeahead to pick
 * a different geo or "Nearby". When a result is selected it navigates to a different page with the same context (i.e.
 * hotels, restaurants, forums, etc) but with the newly selected geo.
 */
const GeoPill = ({ label, servletName, sponsorDataProvider }: Props) => {
  const { usePersistentIcons } = useContext(GlobalNavContext);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);

  const openSearch = useCallback(
    (open: () => mixed) => {
      if (!hasBeenOpened) {
        // track that the user opened the geo picker on the first open
        trackEventAJAX({
          module: `TopNav_${servletName || ''}`,
          action: 'click',
          context: 'GeoPicker',
        });
        setHasBeenOpened(true);
      }
      open();
    },
    [servletName, hasBeenOpened],
  );

  const wrapperClassName = classNames(styles.wrapper, {
    [styles.persistentIcons]: usePersistentIcons,
  });

  return (
    <ResponsiveAttachedOverlay
      position="below"
      alignEdge="left"
      contents={() => (
        <div className={styles.geopill_overlay_container}>
          <GeoPillSearch sponsorDataProvider={sponsorDataProvider} />
        </div>
      )}
    >
      {(target, open, close, isOpen) => (
        <div ref={target} className={wrapperClassName} onClick={() => openSearch(open)}>
          <div className={styles.global_nav_geopill}>
            <span className={`${styles.pill} ui_pill inverted`}>
              {label}
              <Icon name={isOpen ? 'caret-up' : 'caret-down'} className={styles.icon} />
            </span>
          </div>
        </div>
      )}
    </ResponsiveAttachedOverlay>
  );
};

/**
 * The container for the GeoPill which fetches the correct label
 */
// $FlowFixMe92
export default bind((state, stage) => {
  const servletName =
    state.meta && state.meta.initialServletName ? state.meta.initialServletName : '';
  const isSearchServlet = servletName === 'Search';

  // use the location name as our label if we are not looking at the world (unless we are on search in which case we
  // can be scoped to the world)
  const { page } = state;
  const locationId = page.geoId ? page.geoId : page.detailId;
  const useLocationLabel = locationId !== null && (isSearchServlet || locationId !== 1);

  // use the 'Nearby' label if we are on a nearby page or if we have a geoPoint on a search page
  const hasLatLng: boolean = state.page && state.page.geoPoint;
  const isNearbyPage =
    servletName.indexOf('Nearby') !== -1 ||
    (hasLatLng && (isSearchServlet || servletName === 'MobileVacationRentalSearch'));

  // only stage an API call if we need to actually fetch a location name
  let staged;
  if (!isNearbyPage && useLocationLabel) {
    staged = stage(`/data/1.0/location/${locationId}`);
  }

  return () => {
    let label;
    const { data } = staged ? staged.get() : {};

    if (isNearbyPage) {
      label = localize('mx_nearby'); // Nearby
      // If the current location is not geographic then use the parent name.
    } else if (useLocationLabel && data) {
      const isGeographic = data.category && data.category.key && data.category.key === 'geographic';
      label = isGeographic ? data.name : data.parent_display_name;
    }

    // default to 'Worldwide'
    if (!label) {
      label = localize('dual_search_worldwide'); // Worldwide
    }

    return {
      label,
      servletName,
    };
  };
})(GeoPill);
